import type { PLATFORMS } from '@amzn/journeys-common';
import { faMinusCircle, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CampaignType, CardContent, MvtCombination } from 'generated/graphql';
import { type FC, useState } from 'react';
import { Button, Card, Table } from 'react-bootstrap';
import { useForm } from 'react-final-form';
import { stylesheet } from 'typestyle';
import { sizes } from '../../../resources/tokens';
import type { CampaignFormValues } from '../campaign.types';
import {
  getMVTPreviewUrl,
  getMVTVariantHeaderRenderer,
  getMVTVariantValueRenderer,
  getPlatformExcludedMVTExperimentInfo,
} from './MVT.util';

const css = stylesheet({
  'action-button': {
    cursor: 'pointer',
  },
  compact: {
    width: 0,
  },
  container: {
    paddingTop: sizes.large,
  },
  'publish-warn': {
    width: '7rem',
  },
});

type MVTCombinationsProps = {
  combinations?: MvtCombination[];
  excluded?: boolean;
  onPreview?: (totalPreviewed?: number) => void;
  hideAction?: boolean;
  hasPreviews: boolean;
  total: number;
  subtitle?: string;
};

const formatHeader = (header: string) => {
  const result = header.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const MVTCombinations: FC<MVTCombinationsProps> = ({
  excluded,
  onPreview,
  hideAction,
  hasPreviews,
  combinations,
  total,
  subtitle,
}) => {
  const [previewed, setPreviewed] = useState(new Set<string>());
  const actionIcon = excluded ? faPlusCircle : faMinusCircle;
  const form = useForm<CampaignFormValues>();
  const campaign = form.getState().values.campaign;
  const dimensions = campaign.mvtExperimentInfo?.dimensions;
  const typeId = campaign.typeId as CampaignType;
  const content = campaign.content as CardContent;
  const platform = campaign.placements[0].platform as PLATFORMS;

  const showCombination = (isExcluded: boolean) => (excluded ? isExcluded : !isExcluded);

  const getVariantRenderInfo = getMVTVariantValueRenderer(typeId);
  const getVariantHeader = getMVTVariantHeaderRenderer(typeId);
  const platformExclusions = getPlatformExcludedMVTExperimentInfo(typeId)?.get(platform);

  const handleAction = (index: number) => {
    form.change(
      `campaign.mvtExperimentInfo.combinations[${index}].isExcluded` as keyof CampaignFormValues,
      !excluded as unknown as CampaignFormValues[keyof CampaignFormValues],
    );
  };

  const preview = (mvtCombinationId: string, url?: string) => {
    setPreviewed(new Set(previewed.add(mvtCombinationId)));
    onPreview?.(previewed.size);
    if (url) {
      window.open(url, '_blank', 'noreferrer');
    }
  };

  const getPreviewButtonStyle = (mvtCombinationId: string) => (previewed.has(mvtCombinationId) ? 'success' : 'outline-primary');

  const getPreviewButtonText = (mvtCombinationId: string) => (previewed.has(mvtCombinationId) ? 'Previewed' : 'Preview');

  return (
    <div className={css.container}>
      <h5>
        {excluded ? 'Excluded' : 'Selected'} Combinations {subtitle && `(${subtitle})`}
      </h5>
      {total ? (
        <Table borderless hover striped>
          <thead>
            <tr>
              {dimensions?.map((dimension, index) => {
                if (!platformExclusions?.includes(dimension.name)) {
                  if (getVariantHeader) {
                    return getVariantHeader(dimension.name);
                  }
                  return (
                    <th
                      key={`header-${
                        // biome-ignore lint/suspicious/noArrayIndexKey: legacy code
                        index
                      }`}
                    >
                      {formatHeader(dimension.name)}
                    </th>
                  );
                }
                return null;
              })}
              {hasPreviews && <th className={hideAction ? css.compact : ''}>Preview</th>}

              {!hideAction && <th className={css.compact}>Action</th>}
            </tr>
          </thead>
          <tbody>
            {combinations?.map((combination, index) => {
              const mvtCombinationId = combination.id;
              const combinationPreviewUrl = getMVTPreviewUrl(mvtCombinationId, campaign.symphonyInfo);
              if (showCombination(!!combination.isExcluded)) {
                let isCombinationPreviewValid = true;
                return (
                  <tr
                    key={`combination-${
                      // biome-ignore lint/suspicious/noArrayIndexKey: legacy code
                      index
                    }`}
                  >
                    {dimensions?.map((dimension) => {
                      if (!platformExclusions?.includes(dimension.name)) {
                        const variantInfo = getVariantRenderInfo?.(combination, dimension.name, content);
                        if (variantInfo?.fieldPath) {
                          const dirty = form.getFieldState(variantInfo.fieldPath as keyof CampaignFormValues)?.dirty;
                          if (dirty) {
                            isCombinationPreviewValid = false;
                          }
                        }
                        return variantInfo?.render;
                      }
                      return null;
                    })}
                    {hasPreviews && (
                      <td>
                        {!combinationPreviewUrl || !isCombinationPreviewValid ? (
                          <div className={css['publish-warn']}>
                            <i className="fa fa-exclamation-triangle" /> Publish to enable preview
                          </div>
                        ) : (
                          <Button
                            onClick={() => {
                              preview(mvtCombinationId, combinationPreviewUrl);
                            }}
                            variant={getPreviewButtonStyle(mvtCombinationId)}
                          >
                            {getPreviewButtonText(mvtCombinationId)}
                          </Button>
                        )}
                      </td>
                    )}
                    {!hideAction && (
                      <td>
                        <FontAwesomeIcon
                          className={css['action-button']}
                          icon={actionIcon}
                          onClick={() => {
                            handleAction(index);
                          }}
                          size="2x"
                        />
                      </td>
                    )}
                  </tr>
                );
              }
            })}
          </tbody>
        </Table>
      ) : (
        <Card className="text-center">
          <Card.Body>Combinations you are {excluded ? 'removing from' : 'selecting for'} your MVT experiment</Card.Body>
        </Card>
      )}
    </div>
  );
};
